import { useCallback, useEffect, useRef } from 'react';
import { type NavigateOptions, useLocation, useSearchParams } from 'react-router';
import type { SearchValues } from './useQueryParameter';

export function useUpdateQueryParams() {
  const { pathname } = useLocation();
  const [_, setSearchParams] = useSearchParams();
  const fnRef = useRef(setSearchParams);

  const stableFunction = useCallback((...argumentsToSetSearchParams: Parameters<typeof setSearchParams>) => {
    fnRef.current(...argumentsToSetSearchParams);
  }, []);

  // This is necessary because when the pathname changes, the old reference to setSearchParams is preserved, which causes setSearchParams to retain outdated values
  useEffect(() => {
    fnRef.current = setSearchParams;
  }, [pathname, setSearchParams]);

  return useCallback(
    ({
      keepPreviousParams = true,
      navigateOptions,
      urlUpdates,
    }: {
      keepPreviousParams?: boolean;
      navigateOptions?: NavigateOptions;
      urlUpdates: SearchValues;
    }) => {
      stableFunction((prevParams) => {
        const newParams = keepPreviousParams ? new URLSearchParams(prevParams) : new URLSearchParams();

        for (const [key, values] of Object.entries(urlUpdates)) {
          if (values === undefined) {
            newParams.delete(key);
          } else if (Array.isArray(values)) {
            newParams.delete(key);
            for (const value of values) {
              newParams.append(key, value);
            }
          } else {
            newParams.set(key, values);
          }
        }

        return newParams;
      }, navigateOptions);
    },
    [stableFunction],
  );
}
